/**
 * AcenteTrash — admin-only view of soft-deleted reservations that are still
 * inside their 60-second undo window.
 *
 * How this page relates to the reservations page:
 *   - `/acente/rezervasyonlar` shows only LIVE rows and provides the initial
 *     bulk-delete + inline undo toast (single-shot, 60s).
 *   - `/acente/silinen-kayitlar` (this page) gathers ALL trashed rows into
 *     one place so an admin who missed the inline toast can still recover.
 *
 * The list polls the backend every 5 seconds — cheap, always up to date, and
 * the countdown per row is driven by the server-supplied `seconds_remaining`
 * so we don't rely on the client clock.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import panelApi from "@/lib/panelApi";
import { formatTr } from "@/lib/date";
import { Undo2, Trash2, Timer, ArrowLeft, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";

// Polling interval for the trash list. 5 s is short enough to feel live and
// long enough that we never hit the backend hard.
const POLL_MS = 5_000;
// Local countdown tick — pure UX so the number decrements each second even
// between polls. We correct against the server number on every poll.
const TICK_MS = 1_000;

export default function AcenteTrash() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [restoring, setRestoring] = useState(false);
  const [q, setQ] = useState("");

  // Local tick — decrement seconds_remaining every second so the row's
  // countdown feels smooth. Snap back to server value on next poll.
  const [tick, setTick] = useState(0);

  const load = async () => {
    try {
      const r = await panelApi.get("/reservations/trash");
      setRows(r.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Silinen kayıtlar alınamadı");
    } finally {
      setLoading(false);
    }
  };

  // Fetch immediately + poll every 5s.
  useEffect(() => {
    load();
    const p = setInterval(load, POLL_MS);
    return () => clearInterval(p);
  }, []);

  // Local UI tick — increments once a second, purely for the countdown label.
  useEffect(() => {
    const t = setInterval(() => setTick((v) => v + 1), TICK_MS);
    return () => clearInterval(t);
  }, []);

  // Time remaining shown to the user — server number minus local ticks since
  // last poll (bounded to 0). This is intentionally cosmetic: the real state
  // is on the server which purges rows whose window has closed.
  const rowsView = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows
      .map((r) => ({ ...r, seconds_remaining: Math.max(0, (r.seconds_remaining || 0) - (tick % 5)) }))
      .filter((r) => !needle
        || r.reservation_number?.toLowerCase().includes(needle)
        || r.group_name?.toLowerCase().includes(needle)
        || r.hotel_name?.toLowerCase().includes(needle)
        || r.customer_phone?.toLowerCase().includes(needle)
        || r.agency_name?.toLowerCase().includes(needle)
      );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, tick, q]);

  const toggle = (id, on) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(id); else next.delete(id);
      return next;
    });
  };
  const toggleAll = (on) => setSelected(on ? new Set(rowsView.map((r) => r.id)) : new Set());
  const clearSel = () => setSelected(new Set());

  const restore = async (ids) => {
    if (!ids.length) return;
    setRestoring(true);
    try {
      const r = await panelApi.post("/reservations/bulk-restore", { ids });
      const n = r?.data?.restored ?? ids.length;
      toast.success(`${n} rezervasyon geri getirildi`);
      clearSel();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Geri getirilemedi");
    } finally {
      setRestoring(false);
    }
  };

  const totalRemaining = rowsView.length;
  const selectedCount = selected.size;

  return (
    <div className="space-y-5" data-testid="acente-trash-page">
      <div>
        <Link to="/acente/rezervasyonlar" className="text-sm text-brand-navy/60 hover:text-brand-orange inline-flex items-center gap-1">
          <ArrowLeft size={14} /> Rezervasyonlara Dön
        </Link>
        <div className="mt-2 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-rose-600 font-semibold">Yönetici · Çöp Kutusu</div>
            <h1 className="font-serif text-3xl text-brand-navy mt-1">Silinen Rezervasyonlar</h1>
            <p className="text-xs text-slate-500 mt-1">
              Silinen rezervasyonlar 60 saniye boyunca burada saklanır. Süre dolduğunda kalıcı silinirler.
            </p>
          </div>
          <div className="rounded-xl bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-900 flex items-center gap-3" data-testid="trash-stats">
            <div className="w-9 h-9 rounded-lg bg-rose-100 text-rose-700 flex items-center justify-center">
              <Trash2 size={16} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-rose-700 font-semibold">Beklemede</div>
              <div className="font-serif text-2xl leading-none mt-0.5" data-testid="trash-count">{totalRemaining}</div>
            </div>
          </div>
        </div>
      </div>

      {selectedCount > 0 && (
        <div
          className="sticky top-4 z-30 rounded-2xl bg-emerald-700 text-white shadow-xl border border-emerald-800/40 px-4 py-3 flex items-center gap-4 flex-wrap"
          data-testid="trash-bulk-bar"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center">
              <Undo2 size={18} />
            </div>
            <div>
              <div className="text-sm font-medium">
                <span data-testid="trash-selected-count">{selectedCount}</span> rezervasyon seçildi
              </div>
              <div className="text-[11px] text-white/70">Süre bitmeden geri getirebilirsiniz.</div>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={clearSel}
              className="rounded-full bg-white/10 hover:bg-white/20 text-white text-xs px-3 py-1.5"
              data-testid="trash-clear-selection"
            >
              Vazgeç
            </button>
            <button
              onClick={() => restore(Array.from(selected))}
              disabled={restoring}
              className="rounded-full bg-white text-emerald-700 hover:bg-emerald-50 text-xs font-semibold px-4 py-2 inline-flex items-center gap-1 shadow-lg disabled:opacity-70"
              data-testid="trash-restore-selected"
            >
              <Undo2 size={13} /> Seçilenleri Geri Getir
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-brand-navy/10 shadow-sm">
        <div className="p-4 border-b border-slate-100 flex items-center gap-3">
          <Input
            placeholder="Numara / Grup / Otel / Telefon / Acente ara..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="max-w-md"
            data-testid="trash-search"
          />
          {rowsView.length > 0 && (
            <button
              onClick={() => restore(rowsView.map((r) => r.id))}
              disabled={restoring}
              className="ml-auto text-xs px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-800 hover:bg-emerald-200 inline-flex items-center gap-1 disabled:opacity-70"
              data-testid="trash-restore-all"
            >
              <Undo2 size={12} /> Tümünü Geri Getir
            </button>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="trash-table">
            <thead className="text-xs uppercase text-slate-500 border-b border-slate-200 bg-slate-50">
              <tr className="text-left">
                <th className="py-2 pl-3 pr-1 w-8">
                  <input
                    type="checkbox"
                    checked={rowsView.length > 0 && rowsView.every((r) => selected.has(r.id))}
                    ref={(el) => {
                      if (el) {
                        const some = rowsView.some((r) => selected.has(r.id));
                        const all = rowsView.length > 0 && rowsView.every((r) => selected.has(r.id));
                        el.indeterminate = some && !all;
                      }
                    }}
                    onChange={(e) => toggleAll(e.target.checked)}
                    className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                    data-testid="trash-select-all"
                  />
                </th>
                <th className="py-2 px-2">Numara</th>
                <th className="py-2 px-2">Grup / Müşteri</th>
                <th className="py-2 px-2">Otel</th>
                <th className="py-2 px-2">Acente</th>
                <th className="py-2 px-2">Giriş</th>
                <th className="py-2 px-2">Silen</th>
                <th className="py-2 px-2 w-40">Kalan Süre</th>
                <th className="py-2 px-2 w-24"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={9} className="py-6 text-center text-slate-500">Yükleniyor...</td></tr>
              )}
              {!loading && rowsView.length === 0 && (
                <tr>
                  <td colSpan={9} className="py-10 text-center text-slate-500">
                    <div className="inline-flex flex-col items-center gap-2">
                      <div className="w-14 h-14 rounded-full bg-emerald-50 text-emerald-500 flex items-center justify-center">
                        <Trash2 size={22} />
                      </div>
                      <div className="text-sm">Çöp kutusu boş.</div>
                      <div className="text-xs text-slate-400">Silinen rezervasyonlar 60 saniye boyunca burada görünür.</div>
                    </div>
                  </td>
                </tr>
              )}
              {rowsView.map((r) => {
                const checked = selected.has(r.id);
                const sec = r.seconds_remaining;
                const pct = Math.max(0, Math.min(100, (sec / 60) * 100));
                const urgent = sec <= 15;
                return (
                  <tr
                    key={r.id}
                    className={`border-b border-slate-100 hover:bg-slate-50 ${checked ? "bg-emerald-50/60" : ""}`}
                    data-testid={`trash-row-${r.id}`}
                  >
                    <td className="py-3 pl-3 pr-1">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => toggle(r.id, e.target.checked)}
                        className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                        data-testid={`trash-select-${r.id}`}
                      />
                    </td>
                    <td className="py-3 px-2 font-mono text-xs align-top">{r.reservation_number}</td>
                    <td className="py-3 px-2 align-top">
                      <div className="text-sm text-brand-navy">{r.group_name || <span className="text-slate-400 italic">Grup adı yok</span>}</div>
                      {r.customer_phone && <div className="text-[11px] text-slate-500 mt-0.5">📞 {r.customer_phone}</div>}
                    </td>
                    <td className="py-3 px-2 text-sm align-top">{r.hotel_name || "—"}</td>
                    <td className="py-3 px-2 text-sm align-top">{r.agency_name || "—"}</td>
                    <td className="py-3 px-2 text-sm align-top">{formatTr(r.checkin)}</td>
                    <td className="py-3 px-2 text-xs align-top text-slate-600">{r.deleted_by_name || "—"}</td>
                    <td className="py-3 px-2 align-top">
                      <div className={`inline-flex items-center gap-1.5 text-xs font-semibold ${urgent ? "text-rose-700" : "text-emerald-700"}`}>
                        {urgent ? <AlertTriangle size={13} /> : <Timer size={13} />}
                        <span data-testid={`trash-remaining-${r.id}`}>{sec} sn</span>
                      </div>
                      <div className="mt-1 h-1 w-full rounded-full bg-slate-200 overflow-hidden">
                        <div
                          className={`h-full transition-all duration-500 ${urgent ? "bg-rose-500" : "bg-emerald-500"}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </td>
                    <td className="py-3 px-2 text-right align-top">
                      <button
                        onClick={() => restore([r.id])}
                        disabled={restoring || sec <= 0}
                        className="text-xs px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-800 hover:bg-emerald-200 inline-flex items-center gap-1 disabled:opacity-50"
                        data-testid={`trash-restore-${r.id}`}
                      >
                        <Undo2 size={12} /> Geri Getir
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
